import { useState } from "react";
import { Check, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";
import { contactUrl, NEEDS, PLAN_FEATURES, setLicense, useLicense } from "../lib/license";

const CORE = ["inventory", "consignment", "sales", "leads", "reservations", "forms", "reports", "backups", "two_factor"];
const fmt = (d) => (!d ? "—" : String(d).length === 10 ? new Date(`${d}T00:00:00`).toLocaleDateString() : new Date(d).toLocaleString());

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[var(--divider-soft)] text-sm">
      <span className="opacity-70">{label}</span><span className="font-medium text-end" data-testid={testid}>{value}</span>
    </div>
  );
}

function Features({ lic }) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2" data-testid="plan-features">
      {PLAN_FEATURES.map((f) => {
        const on = Boolean(lic.flags?.[f]);
        return (
          <li key={f} className="flex items-center gap-2 text-sm" data-testid={`plan-feature-${f}`} data-on={on ? "1" : "0"}>
            {on ? <Check size={15} className="text-green-700" /> : <Lock size={15} className="opacity-60" />}
            <span>{tr(`lic.f_${f /* i18n-keys: website_themes_all,online_payments,social_posting,ai_creatives,multi_branch,whatsapp_crm */}`)}</span>
            {!on && <span className="text-xs opacity-60">· {tr("lic.available_on", { plan: tr(`lic.plan_${NEEDS[f] /* i18n-keys: growth,pro */}`) })}</span>}
          </li>
        );
      })}
      {CORE.map((f) => (
        <li key={f} className="flex items-center gap-2 text-sm" data-testid={`plan-core-${f}`}>
          <Check size={15} className="text-green-700" /><span>{tr(`lic.core_${f /* i18n-keys: inventory,consignment,sales,leads,reservations,forms,reports,backups,two_factor */}`)}</span>
        </li>
      ))}
    </ul>
  );
}

function Summary({ lic }) {
  const u = lic.usage || {};
  return (
    <div>
      {lic.showroom_name && <Row label={tr("lic.showroom")} value={lic.showroom_name} testid="plan-showroom-name" />}
      <Row label={tr("lic.plan")} value={tr(`lic.plan_${lic.plan /* i18n-keys: starter,growth,pro */}`) + (lic.partner ? ` · ${tr("lic.partner")}` : "")} testid="plan-name" />
      <Row label={tr("lic.status")} value={lic.mode === "dev" ? tr("lic.dev_mode") : tr(`lic.status_${lic.status /* i18n-keys: active,past_due,read_only,suspended */}`)} testid="plan-status" />
      <Row label={tr("lic.users")} value={tr("lic.x_of_y", { x: u.users ?? "—", y: u.max_users ?? "—" })} testid="plan-users" />
      <Row label={tr("lic.ai_images")} value={tr("lic.x_of_y", { x: u.ai_images ?? 0, y: u.ai_image_limit ?? 0 })} testid="plan-ai-images" />
      <Row label={tr("lic.paid_until")} value={fmt(lic.paid_until)} testid="plan-paid-until" />
      {lic.grace_ends && <Row label={tr("lic.grace_ends")} value={fmt(lic.grace_ends)} testid="plan-grace-ends" />}
      <Row label={tr("lic.last_check")} value={fmt(lic.last_check_at)} testid="plan-last-check" />
      <Row label={tr("lic.last_good")} value={fmt(lic.last_good_at)} testid="plan-last-good" />
    </div>
  );
}

export const MyPlanPanel = () => {
  const { lic } = useLicense();
  const [busy, setBusy] = useState(false);
  if (!lic) return <div className="opacity-60 text-sm">{tr("x.loading")}</div>;
  const url = contactUrl(lic);
  const refresh = async () => {
    setBusy(true);
    try { setLicense((await api.post("/license/refresh")).data); toast.success(tr("lic.refreshed")); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-6" data-testid="my-plan-panel">
      {lic.mode === "dev" && <p className="text-sm p-3" style={{ background: "var(--surface)" }} data-testid="plan-dev-note">{tr("lic.dev_note")}</p>}
      {lic.cant_verify && lic.mode !== "dev" && <p className="text-sm p-3" style={{ background: "#fff6d6" }} data-testid="plan-cant-verify">{tr("lic.banner_cant_verify")}</p>}
      {lic.last_error && <p className="text-xs opacity-70" data-testid="plan-last-error">{tr("lic.last_error")}: {lic.last_error}</p>}
      <Summary lic={lic} />
      <Features lic={lic} />
      <div className="flex flex-wrap gap-2">
        {url ? <a className="btn-primary" href={url} target="_blank" rel="noopener noreferrer" data-testid="plan-upgrade-btn">{tr("lic.upgrade_renew")}</a>
          : <span className="text-sm opacity-70 self-center" data-testid="plan-no-contact">{tr("lic.contact_wafa")}</span>}
        <button className="btn-secondary" onClick={refresh} disabled={busy} data-testid="plan-refresh-btn"><RefreshCw size={14} /> {tr("lic.refresh")}</button>
      </div>
    </div>
  );
};
